import { Suspense, lazy, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { formatDateMMDDYYYY, getReturnColor } from '@/lib/utils';
import LiveSmartSignalSidebar from '@/components/LiveSmartSignalSidebar';
import StockParallaxBackdrop from '@/components/StockParallaxBackdrop';
import Reveal from '@/components/Reveal';

/**
 * Public (logged-out) homepage. Reads pre-computed values from `homepage_cache`
 * so cold traffic gets full content within a single round-trip — no per-visit
 * RPC fan-out.
 *
 * Cache is refreshed hourly by `refresh-homepage-cache` edge function (pg_cron).
 *
 * Live cells (cached, recomputed hourly):
 *   - Hero #1: Mean 90d return     (full dip-buy pool)
 *   - Hero #2: Mean alpha vs SPY   (full dip-buy pool)
 *   - Top 5 leaderboard preview    (HK-filtered)
 *   - Recent high-conviction cards (HK-filtered)
 *
 * Static cells:
 *   - Hero #3: "2,900+ dip-buy signals tracked"
 *   - Hero #4: "40+ insiders ranked"
 *   - Footer: "Updated hourly"
 *
 * HK filter: HORIZON KINETICS ASSET MANAGEMENT LLC + STAHL MURRAY are excluded
 * from leaderboard preview & signal cards because their concentrated TPL
 * position skews cold-traffic first impressions. Hero stats use the full pool —
 * those are the headline disclosure numbers and Track Record discloses the
 * breakdown.
 *
 * Navbar is rendered by the parent so this component can drop into pages that
 * already render Navbar (e.g. Index.tsx).
 */

interface HeroStats {
  r90_mean: number | null;
  win_rate_pct: number | null;
  tracked_buys_count?: number | null;
  settled_count?: number | null;
  insiders_graded_count?: number | null;
  // Methodology v3
  peaked_above_spy_pct?: number | null;
  avg_insider_peak?: number | null;
  avg_spy_peak_same_windows?: number | null;
  outperformance_multiplier?: number | null;
}

interface LeaderRow {
  filer_name: string;
  filer_title: string | null;
  win_rate: number;
  wins: number;
  trades: number;
  mean_return_90: number | null;
  last_buy_date: string | null;
}

interface SignalRow {
  ticker: string;
  filer_name: string;
  flag_reason: string;
  entry_price: number;
  peak_return_90d: number | null;
  days_to_peak_90d: number | null;
  peak_window_complete: boolean;
  filing_date: string;
}

interface HomepageCache {
  hero: HeroStats;
  leaders: LeaderRow[];
  signals: SignalRow[];
  generated_at: string;
}

declare global {
  interface Window {
    __homepageCachePromise?: Promise<HomepageCache | null>;
    __homepageCacheValue?: HomepageCache;
  }
}

const FALLBACK: HomepageCache = {
  hero: {
    r90_mean: 8.22,
    win_rate_pct: 3.30,
    tracked_buys_count: null,
    insiders_graded_count: null,
    settled_count: 453,
    peaked_above_spy_pct: 68.7,
    avg_insider_peak: 22.08,
    avg_spy_peak_same_windows: 5.45,
    outperformance_multiplier: 4.05,
  },
  leaders: [],
  signals: [],
  generated_at: new Date().toISOString(),
};

const DeferredHomepageSections = lazy(() => import('@/components/PublicHomepageSections'));

const fmtPct = (n: number | null | undefined, withSign = true) => {
  if (n == null) return '—';
  // Display cap: extreme outliers render as ±200%+
  if (n > 200) return withSign ? '+200%+' : '200%+';
  if (n < -200) return '-200%+';
  const sign = withSign && n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(n >= 100 || n <= -100 ? 0 : 2)}%`;
};

const daysSince = (iso: string) => {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};



const PublicHomepage = () => {
  const [data, setData] = useState<HomepageCache>(FALLBACK);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof globalThis.setTimeout> | undefined;
    let idleId: number | undefined;

    const loadHomepageCache = async () => {
      try {
        const browserWindow = typeof window !== 'undefined' ? window : undefined;

        if (browserWindow?.__homepageCacheValue) {
          if (!cancelled) setData(browserWindow.__homepageCacheValue);
          return;
        }

        if (browserWindow?.__homepageCachePromise) {
          const prefetched = await browserWindow.__homepageCachePromise;
          if (!cancelled && prefetched) {
            setData(prefetched);
            return;
          }
        }

        const { data: row } = await supabase
          .from('homepage_cache')
          .select('value')
          .eq('cache_key', 'homepage_v1')
          .maybeSingle<{ value: HomepageCache }>();
        if (!cancelled && row?.value) setData(row.value);
      } catch (e) {
        console.error('homepage_cache load error:', e);
      }
    };

    const browserWindow = typeof window !== 'undefined' ? window : undefined;

    if (browserWindow && 'requestIdleCallback' in browserWindow) {
      idleId = browserWindow.requestIdleCallback(() => {
        void loadHomepageCache();
      });
    } else {
      timeoutId = globalThis.setTimeout(() => {
        void loadHomepageCache();
      }, 1);
    }

    return () => {
      cancelled = true;
      if (idleId !== undefined && browserWindow && 'cancelIdleCallback' in browserWindow) {
        browserWindow.cancelIdleCallback(idleId);
      }
      if (timeoutId !== undefined) {
        globalThis.clearTimeout(timeoutId);
      }
    };
  }, []);

  const { hero, leaders, signals } = data;

  const returnClass = (n: number | null) =>
    n == null ? 'text-muted-foreground' : n >= 0 ? 'text-positive' : 'text-negative';

  return (
    <>
      {/* ─── HERO — message-matched to ad creative ─── */}
      <section className="relative overflow-hidden border-b border-border/50 bg-background pt-4 pb-8 md:pt-12 md:pb-14">
        <StockParallaxBackdrop />
        <div className="relative z-10 container mx-auto max-w-6xl px-4 md:px-6 lg:max-w-7xl lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            {/* Eyebrow with live pulse */}
            <Reveal className="mb-3 flex items-center justify-center gap-2 md:mb-5">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-live-dot" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                SEC Form 4 · Live Feed
              </span>
            </Reveal>

            <Reveal delay={0.06}>
              <h1 className="font-sans text-[26px] font-semibold leading-[1.05] tracking-[-0.025em] text-foreground sm:text-3xl md:text-4xl lg:text-[44px]">
                Track insider and Congress stock filings —
                <span className="block text-buy">fast, filtered, in one place.</span>
              </h1>
            </Reveal>

            <Reveal delay={0.12}>
              <p className="mx-auto mt-3 max-w-xl text-[13px] leading-snug text-muted-foreground md:mt-5 md:text-[15px] md:leading-relaxed">
                See what U.S. executives and members of Congress are buying — in real time, with every trade's return tracked against the S&amp;P 500.
              </p>
            </Reveal>

            {/* Primary CTA + stacked secondary link + trust line */}
            <Reveal delay={0.18} className="mt-5 flex flex-col items-center gap-2 md:mt-7">
              <Link
                to="/signup"
                className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-6 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
              >
                Start tracking — free
              </Link>
              <p className="text-[11px] text-muted-foreground">
                Free to start — no credit card required.
              </p>
              <Link
                to="/track-record"
                className="mt-1 inline-flex min-h-[44px] items-center gap-1 px-3 py-2.5 text-[13px] font-normal text-primary underline-offset-2 hover:underline"
              >
                See public track record <ArrowRight className="h-3 w-3" />
              </Link>
            </Reveal>

            {/* Proof statement */}
            <Reveal delay={0.24}>
              <p className="mx-auto mt-6 max-w-xl text-[13px] leading-relaxed text-muted-foreground md:mt-8 md:text-[15px]">
                <span className="font-semibold text-foreground">
                  {hero.peaked_above_spy_pct != null ? `${hero.peaked_above_spy_pct.toFixed(1)}%` : '68.7%'}
                </span>{' '}
                of tracked insider buys peaked above SPY in the same 90-day window — n=
                {hero.settled_count != null ? hero.settled_count.toLocaleString() : '453'}
                , settled since launch.{' '}
                <Link to="/track-record" className="text-primary hover:underline">
                  See methodology →
                </Link>
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Live Buys terminal — full-width under hero, anchored to text column width.
          The id is consumed by the homepage sticky CTA on mobile to delay reveal
          until the user scrolls past this section's bottom edge. */}
      <section id="live-buys-anchor" className="border-b border-border/50 bg-background py-6 md:py-8">
        <div className="container mx-auto max-w-3xl px-4 md:px-6">
          <Reveal delay={0.05}>
            <LiveSmartSignalSidebar />
          </Reveal>
        </div>
      </section>

      <Suspense fallback={null}>
        <DeferredHomepageSections
          leaders={leaders}
          signals={signals}
          returnClass={returnClass}
          fmtPct={fmtPct}
          formatDate={formatDateMMDDYYYY}
          daysSince={daysSince}
        />
      </Suspense>
    </>
  );
};

export default PublicHomepage;

